import { useState, useRef, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useMarqueesByDeviceId } from '@/hooks/useMarqueesByDeviceId'
import { marqueeService } from '@/services/marquee_service'
import { Marquee } from '@magnetar/data-access'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { GripVertical, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import { logger } from '@magnetar/utils'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

type MarqueeItemProps = {
  marquee: Marquee
  onUpdate: (id: number, content: string) => void
  onDelete: (id: number) => void
}

function SortableMarqueeItem({ marquee, onUpdate, onDelete }: MarqueeItemProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [content, setContent] = useState(marquee.marquee_content ?? '')
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [originalContent, setOriginalContent] = useState(marquee.marquee_content ?? '')
  const inputRef = useRef<HTMLInputElement>(null)

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: marquee.marquee_id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : 0,
  }

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isEditing])

  const handleSaveTemp = () => {
    setIsEditing(false)
    onUpdate(marquee.marquee_id, content)
    setOriginalContent(content)
  }

  const handleBlur = () => {
    if (content !== originalContent) {
      setShowUnsavedDialog(true)
    } else {
      setIsEditing(false)
    }
  }

  const handleSaveChanges = () => {
    handleSaveTemp()
    setShowUnsavedDialog(false)
  }

  const handleDiscardChanges = () => {
    setContent(originalContent)
    setIsEditing(false)
    setShowUnsavedDialog(false)
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 rounded-md border p-4 ${isDragging ? 'bg-accent' : 'bg-background'}`}
    >
      {isEditing ? (
        <>
          <div className="flex-1">
            <Input
              ref={inputRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onBlur={handleBlur}
            />
          </div>
          <Button onClick={handleSaveTemp}>暫存</Button>
        </>
      ) : (
        <>
          <div
            onClick={() => setIsEditing(true)}
            className="flex-1 cursor-pointer whitespace-pre-wrap"
          >
            {content || '點擊輸入內容'}
          </div>
          <Button variant="ghost" size="icon" onClick={() => setShowDeleteDialog(true)}>
            <Trash2 className="h-4 w-4" />
          </Button>
          <button className="cursor-grab touch-none" {...attributes} {...listeners}>
            <GripVertical className="h-5 w-5 text-muted-foreground" />
          </button>
        </>
      )}

      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>是否要儲存變更？</AlertDialogTitle>
            <AlertDialogDescription>您有未儲存的變更。是否要儲存這些變更？</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDiscardChanges}>不儲存</AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveChanges}>儲存變更</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除嗎？</AlertDialogTitle>
            <AlertDialogDescription>
              此動作無法復原。刪除後，跑馬燈將永久移除。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteDialog(false)}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDelete(marquee.marquee_id)
                setShowDeleteDialog(false)
              }}
            >
              確定刪除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

type MarqueeSettingProps = {
  broadcastDeviceId?: number
}

export function MarqueeSetting({ broadcastDeviceId }: MarqueeSettingProps) {
  const { token } = useAuth()
  const [localMarquees, setLocalMarquees] = useState<Marquee[]>([])
  const [newContent, setNewContent] = useState('')
  const [hasChanges, setHasChanges] = useState(false)
  const [isAdding, setIsAdding] = useState(false)

  const { marquees, mutate } = useMarqueesByDeviceId({
    broadcast_device_id: broadcastDeviceId,
  })

  useEffect(() => {
    if (marquees) {
      setLocalMarquees(marquees)
    }
  }, [marquees])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setLocalMarquees((items) => {
        const oldIndex = items.findIndex((item) => item.marquee_id === active.id)
        const newIndex = items.findIndex((item) => item.marquee_id === over.id)

        // 先移動項目
        const newItems = arrayMove(items, oldIndex, newIndex)

        // 更新所有項目的 order 值
        return newItems.map((item, index) => ({
          ...item,
          order: index, // 根據新的位置設置 order
        }))
      })
      setHasChanges(true)
    }
  }

  const handleUpdate = (id: number, content: string) => {
    setLocalMarquees((prev) =>
      prev.map((item) => (item.marquee_id === id ? { ...item, marquee_content: content } : item)),
    )
    setHasChanges(true)
  }

  const handleDelete = (id: number) => {
    setLocalMarquees((prev) => prev.filter((item) => item.marquee_id !== id))
    setHasChanges(true)
  }

  const handleAddNew = () => {
    if (!newContent.trim() || !broadcastDeviceId) return

    // 為新增的跑馬燈設置臨時 ID (負數，避免與資料庫 ID 衝突)
    const tempId = -Date.now()
    const newMarquee: Partial<Marquee> & { broadcast_device_id: number; marquee_id: number } = {
      marquee_id: tempId, // 使用臨時 ID
      broadcast_device_id: broadcastDeviceId,
      marquee_content: newContent,
      marquee_switching_time: 5000, // 預設5秒
      is_active: true,
      order: localMarquees.length, // 新增的跑馬燈放在最後
      created_at: new Date(),
    }

    setLocalMarquees((prev) => [...prev, newMarquee as Marquee])
    setNewContent('')
    setIsAdding(false)
    setHasChanges(true)
  }

  const handleSave = async () => {
    if (!token || !broadcastDeviceId) return

    try {
      // 準備批次更新的數據
      const marquees = localMarquees.map((marquee, index) => ({
        // 如果是臨時 ID (負數)，則不傳送 marquee_id
        marquee_id: marquee.marquee_id && marquee.marquee_id > 0 ? marquee.marquee_id : undefined,
        marquee_content: marquee.marquee_content ?? '',
        marquee_switching_time: marquee.marquee_switching_time,
        is_active: marquee.is_active,
        order: index,
      }))

      logger.log('準備更新跑馬燈:', {
        total: marquees.length,
        new: marquees.filter((m) => !m.marquee_id).length,
        existing: marquees.filter((m) => m.marquee_id).length,
      })

      // 呼叫批次更新 API
      const result = await marqueeService.batchUpdateMarquees(broadcastDeviceId, marquees, token)

      if (result.success) {
        logger.log('更新跑馬燈成功')
        toast.success('跑馬燈更新成功')
        mutate()
        setHasChanges(false)
      } else {
        logger.error('更新跑馬燈失敗:', result.error)
        toast.error(result.error?.message || '跑馬燈更新失敗')
      }
    } catch (error) {
      logger.error('更新跑馬燈時發生錯誤:', error)
      toast.error(error instanceof Error ? error.message : '跑馬燈更新時發生錯誤')
    }
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold tracking-tight">跑馬燈設定</h2>

      <div className="space-y-4">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={localMarquees.map((m) => m.marquee_id)}
            strategy={verticalListSortingStrategy}
          >
            {localMarquees.map((marquee, index) => (
              <SortableMarqueeItem
                key={marquee.marquee_id || index}
                marquee={marquee}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
              />
            ))}
          </SortableContext>
        </DndContext>

        <div className="flex items-center gap-2 rounded-md border p-4">
          {isAdding ? (
            <>
              <div className="flex-1">
                <Input
                  value={newContent}
                  onChange={(e) => setNewContent(e.target.value)}
                  placeholder="輸入新的跑馬燈內容"
                  autoFocus
                />
              </div>
              <Button onClick={handleAddNew}>暫存</Button>
            </>
          ) : (
            <div
              className="flex flex-1 cursor-pointer items-center justify-center gap-2 py-2"
              onClick={() => setIsAdding(true)}
            >
              <Plus className="h-5 w-5 text-muted-foreground" />
              <span className="text-muted-foreground">新增跑馬燈</span>
            </div>
          )}
        </div>

        {hasChanges && (
          <div className="flex justify-end">
            <Button onClick={handleSave}>儲存變更</Button>
          </div>
        )}
      </div>
    </div>
  )
}
